import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, query, where, doc, getDoc, updateDoc, limit, startAfter, orderBy, addDoc, Timestamp, deleteDoc, setDoc } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';
import '../style/OrdersPage.css';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import axios from 'axios';
import RaiseTicketModal from '../components/RaiseTicket';

const PaidOrdersPage = () => {
    const [paidOrders, setPaidOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editedOrderData, setEditedOrderData] = useState({});
    const [isSearching, setIsSearching] = useState(false);
    const [trackingLoading, setTrackingLoading] = useState({});

    // New state for ticket modal
    const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
    const [selectedOrderForTicket, setSelectedOrderForTicket] = useState(null);
    
    // New state for modal type
    const [modalType, setModalType] = useState('view'); // 'view', 'cancel', 'edit'
    const [processingCancel, setProcessingCancel] = useState(false);
    const [processingSave, setProcessingSave] = useState(false);
    
    // Enhanced pagination state
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [allOrdersLoaded, setAllOrdersLoaded] = useState(false); // Track if all orders are loaded
    const [filteredHasMore, setFilteredHasMore] = useState(true); // Track pagination for filtered results
    const PAGE_SIZE = 20;
    
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const user = auth.currentUser;
    if (!user) {
        throw new Error("User not logged in");
    }

    const checkOrderShippingStatus = async (orderId) => {
        try {
            const confirmedOrdersRef = collection(db, 'users', user.uid, 'confirmed_orders');
            
            // Query for channel_order_id
            const q1 = query(confirmedOrdersRef, where('channel_order_id', '==', orderId));
            const snapshot1 = await getDocs(q1);
            
            // Query for data.refrence_id
            const q2 = query(confirmedOrdersRef, where('data.refrence_id', '==', orderId));
            const snapshot2 = await getDocs(q2);
            
            // Check if either query found results
            if (!snapshot1.empty) {
                const orderDoc = snapshot1.docs[0];
                const orderData = orderDoc.data();
                return orderData.shipping_status !== 'shipped';
            }
            
            if (!snapshot2.empty) {
                const orderDoc = snapshot2.docs[0];
                const orderData = orderDoc.data();
                return orderData.shipping_status !== 'shipped';
            }
            
            return true; // If no confirmed order found, allow cancellation
        } catch (error) {
            console.error("Error checking shipping status:", error);
            return false; // If error, don't allow cancellation
        }
    };

    const fetchTrackingData = async (orderId) => {
        try {
            const response = await axios.get(`https://ufb-1.onrender.com/api/track-order?order_id=${orderId}`);
            
            if (response.data && response.data.tracking_url) {
                // Update the order with tracking URL in local state
                setPaidOrders(prevOrders => 
                    prevOrders.map(order => 
                        order.id === orderId 
                            ? { ...order, tracking_url: response.data.tracking_url } 
                            : order
                    )
                );
                
                // Also update filtered orders
                setFilteredOrders(prevFiltered => 
                    prevFiltered.map(order => 
                        order.id === orderId 
                            ? { ...order, tracking_url: response.data.tracking_url } 
                            : order
                    )
                );
                
                // Open the tracking URL in a new tab
                window.open(response.data.tracking_url, '_blank');
                
                return {
                    success: true,
                    tracking_url: response.data.tracking_url,
                };
            } else {
                return {
                    success: false,
                    tracking_url: null,
                };
            }
        } catch (error) {
            console.error("Error fetching tracking data for order", orderId, error);
            return {
                success: false,
                tracking_url: null,
            };
        }
    };


// A helper function to parse date strings ('YYYY-MM-DD') into UTC Date objects
    const parseDateToUTC = (dateString, atEndOfDay = false) => {
        if (!dateString || typeof dateString !== 'string') return null;
    
        const parts = dateString.split('-');
        if (parts.length !== 3) return null; // Invalid format
    
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
        const day = parseInt(parts[2], 10);
    
        if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    
        if (atEndOfDay) {
            // Returns a Date object for the very end of the specified day in UTC
            return new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
        } else {
            // Returns a Date object for the very start of the specified day in UTC
            return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
        }
    };

    // Enhanced fetch function that considers filters
const fetchPaidOrders = async (isLoadMore = false, applyFilters = false) => {
    console.log("--- [DEBUG] Starting fetchPaidOrders ---");
    console.log(`Filters Active: ${applyFilters}, Start Date: '${startDate}', End Date: '${endDate}'`);

    try {
        if (isLoadMore) {
            setLoadingMore(true);
        } else {
            if (applyFilters) setFilteredOrders([]);
            setLoading(true);
        }
        
        const ordersCollection = collection(db, 'users', user.uid, 'orders');
        
        let queryConstraints = [
            where('isPaid', '==', true),
            where('status', '==', 'confirmed'),
            orderBy('updatedAt', 'desc') // We are correctly ordering by the update date
        ];

        // *** FINAL DATE OBJECT-BASED FILTERING LOGIC ***
        if (applyFilters && (startDate || endDate)) {
            let finalStartDate = startDate;
            let finalEndDate = endDate;

            if (startDate && !endDate) {
                const today = new Date();
                const year = today.getFullYear();
                const month = String(today.getMonth() + 1).padStart(2, '0');
                const day = String(today.getDate()).padStart(2, '0');
                finalEndDate = `${year}-${month}-${day}`;
            }
            
            if (finalStartDate) {
                // Create a JS Date object. The SDK will convert this to a Firestore Timestamp.
                const startDateTime = new Date(`${finalStartDate}T00:00:00.000Z`);
                console.log(`[DEBUG] Adding constraint: updatedAt >=`, startDateTime);
                queryConstraints.push(where('updatedAt', '>=', startDateTime));
            }
            
            if (finalEndDate) {
                // Create a JS Date object for the end of the day.
                const endDateTime = new Date(`${finalEndDate}T23:59:59.999Z`);
                console.log(`[DEBUG] Adding constraint: updatedAt <=`, endDateTime);
                queryConstraints.push(where('updatedAt', '<=', endDateTime));
            }
        }
        
        if (isLoadMore && lastVisible) {
            queryConstraints.push(startAfter(lastVisible));
        }
        
        queryConstraints.push(limit(PAGE_SIZE));
        
        const q = query(ordersCollection, ...queryConstraints);
        
        console.log("[DEBUG] Executing query...");
        const ordersSnapshot = await getDocs(q);
        console.log(ordersSnapshot);
        console.log(`[DEBUG] Query complete. Found ${ordersSnapshot.docs.length} documents.`);
        
        const hasMoreResults = ordersSnapshot.docs.length === PAGE_SIZE;
        
        if (applyFilters) setFilteredHasMore(hasMoreResults);
        else setHasMore(hasMoreResults);
        
        if (ordersSnapshot.docs.length > 0) {
            setLastVisible(ordersSnapshot.docs[ordersSnapshot.docs.length - 1]);
        }
        
        const ordersList = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        if (isLoadMore) {
            setFilteredOrders(prev => [...prev, ...ordersList]);
            if (!applyFilters) setPaidOrders(prev => [...prev, ...ordersList]);
        } else {
            setFilteredOrders(ordersList);
            if (!applyFilters) setPaidOrders(ordersList);
        }
        
    } catch (error) {
        console.error("--- [CRITICAL ERROR] Error fetching paid orders: ---", error);
        alert("Failed to load orders. Please check the developer console for error details.");
    } finally {
        setLoading(false);
        setLoadingMore(false);
        console.log("--- [DEBUG] fetchPaidOrders finished ---");
    }
};

    const getAwbNumber = async (orderId) => {
        try {
            console.log("Fetching AWB for order:", orderId);
            console.log("User UID:", user.uid);
            
            const confirmedOrderDocRef = doc(db, 'users', user.uid, 'confirmed_orders', orderId);
            const confirmedOrderDoc = await getDoc(confirmedOrderDocRef);
            
            console.log("Document exists:", confirmedOrderDoc.exists());
            
            if (confirmedOrderDoc.exists()) {
                const orderData = confirmedOrderDoc.data();
                console.log("Order data:", orderData);
                console.log("AWB Number:", orderData.awbNumber);
                return orderData.awbNumber || null;
            } else {
                console.error("No confirmed_orders document found for order ID:", orderId);
                return null;
            }
        } catch (error) {
            console.error("Error fetching AWB number from confirmed_orders:", error);
            return null;
        }
    };

    const handleTrackOrder = async (orderId, e) => {
        e.stopPropagation();
        
        try {
            setTrackingLoading(prev => ({ ...prev, [orderId]: true }));
            
            console.log("Attempting original tracking method for order:", orderId);
            const originalTrackingResult = await fetchTrackingData(orderId);
            
            if (originalTrackingResult && originalTrackingResult.success) {
                console.log("Original tracking successful");
                return;
            }
            
            console.log("Original tracking failed, attempting AWB tracking method");
            
            const awbNumber = await getAwbNumber(orderId);
            
            if (awbNumber) {
                const shipmozoTrackingUrl = `https://panel.shipmozo.com/track-order?awb=${awbNumber}`;
                
                setPaidOrders(prevOrders => 
                    prevOrders.map(order => 
                        order.id === orderId 
                            ? { ...order, tracking_url: shipmozoTrackingUrl } 
                            : order
                    )
                );
                
                setFilteredOrders(prevFiltered => 
                    prevFiltered.map(order => 
                        order.id === orderId 
                            ? { ...order, tracking_url: shipmozoTrackingUrl } 
                            : order
                    )
                );
                
                window.open(shipmozoTrackingUrl, '_blank');
                console.log("AWB tracking successful");
            } else {
                alert("Tracking information not available for this order.");
                console.error("Both tracking methods failed for order:", orderId);
            }
            
        } catch (error) {
            console.error("Error in handleTrackOrder for order", orderId, error);
            alert("Failed to fetch tracking information. Please try again.");
        } finally {
            setTrackingLoading(prev => ({ ...prev, [orderId]: false }));
        }
    };

    const handleRaiseTicket = (order, e) => {
        e.stopPropagation();
        setSelectedOrderForTicket(order);
        setIsTicketModalOpen(true);
    };

    const closeTicketModal = () => {
        setIsTicketModalOpen(false);
        setSelectedOrderForTicket(null);
    };

    const searchOrderById = async (searchId) => {
        try {
            setIsSearching(true);
            
            const ordersCollection = collection(db, 'users', user.uid, 'orders');
            let results = [];
            
            if (searchId.trim()) {
                try {
                    const orderDoc = doc(db, 'users', user.uid, 'orders', searchId.trim());
                    const orderSnapshot = await getDocs(query(
                        ordersCollection,
                        where('__name__', '==', searchId.trim()),
                        where('status', '==', 'confirmed'),
                        where('isPaid', '==', true)
                    ));
                    
                    if (!orderSnapshot.empty) {
                        const orderData = orderSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        results = [...results, ...orderData];
                    }
                } catch (err) {
                    console.log("Error searching by document ID:", err);
                }
                
                try {
                    const storeOrderSnapshot = await getDocs(query(
                        ordersCollection,
                        where('Storeorder_id', '==', searchId.trim()),
                        where('isPaid', '==', true)
                    ));
                    
                    if (!storeOrderSnapshot.empty) {
                        const storeOrderData = storeOrderSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        results = [...results, ...storeOrderData];
                    }
                } catch (err) {
                    console.log("Error searching by store order ID:", err);
                }
                
                if (results.length === 0) {
                    try {
                        const allOrdersSnapshot = await getDocs(query(
                            ordersCollection,
                            where('isPaid', '==', true),
                            where('status', '==', 'confirmed'),
                        ));
                        
                        const containsMatches = allOrdersSnapshot.docs
                            .map(doc => ({ id: doc.id, ...doc.data() }))
                            .filter(order => 
                                (order.Storeorder_id && order.Storeorder_id.includes(searchId.trim())) ||
                                order.id.includes(searchId.trim())
                            );
                        
                        results = [...results, ...containsMatches];
                    } catch (err) {
                        console.log("Error performing contains search:", err);
                    }
                }
            }
            
            setFilteredOrders(results);
            setIsSearching(false);
        } catch (error) {
            console.error("Error searching for order:", error);
            setIsSearching(false);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        // Reset pagination state when search changes
        setLastVisible(null);
        setHasMore(true);
        setFilteredHasMore(true);
        
        if (value.trim() === '') {
            setFilteredOrders(paidOrders);
        } else {
            const localFiltered = paidOrders.filter(order => 
                order.id.includes(value) || 
                (order.Storeorder_id && order.Storeorder_id.includes(value))
            );
            
            setFilteredOrders(localFiltered);
            
            if (value.trim().length >= 3) {
                const timeoutId = setTimeout(() => {
                    searchOrderById(value);
                }, 500);
                
                return () => clearTimeout(timeoutId);
            }
        }
    };

    // Enhanced load more function
    const handleLoadMore = () => {
        const hasActiveFilters = startDate || endDate || searchTerm.trim();
        
        if (hasActiveFilters) {
            // If there are active filters, we need to load more with those filters
            if (filteredHasMore && !loadingMore) {
                if (startDate && endDate) {
                    fetchPaidOrders(true, true); // Load more with date filters
                } else if (searchTerm.trim()) {
                    // For search, we might need different logic
                    // For now, we'll indicate no more results for search
                    setFilteredHasMore(false);
                }
            }
        } else {
            // No filters active, load more normally
            if (hasMore && !loadingMore) {
                fetchPaidOrders(true, false);
            }
        }
    };

    const handleDownloadInvoice = (invoiceUrl) => {
        if (invoiceUrl) {
            window.open(invoiceUrl, '_blank');
        } else {
            alert('Invoice not available for this order.');
        }
    };

    const openOrderModal = async (order) => {
        const canCancel = await checkOrderShippingStatus(order.id);
        setSelectedOrder(order);
        setEditedOrderData({ ...order });
        setModalType(canCancel ? 'view' : 'view_only');
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedOrder(null);
        setEditedOrderData({});
        setModalType('view');
    };

    const handleOrderDataChange = (field, value) => {
        setEditedOrderData(prevData => ({
            ...prevData,
            [field]: value
        }));
    };

    const handleLineItemChange = (index, field, value) => {
        setEditedOrderData(prevData => ({
            ...prevData,
            order_items: prevData.order_items.map((item, i) => 
                i === index ? { ...item, [field]: value } : item
            )
        }));
    };

    const handleCancelOrder = async () => {
        if (!selectedOrder) return;

        if (selectedOrder.status === 'cancelled') {
            alert("This order has already been cancelled.");
            return;
        }

        try {
            setProcessingCancel(true);

            const orderRef = doc(db, 'users', user.uid, 'orders', selectedOrder.id);
            await updateDoc(orderRef, {
                status: 'cancelled',
                cancelledAt: Timestamp.now()
            });

            const walletRef = doc(db, 'users', user.uid, 'wallet', 'walletDetails');
            const walletSnap = await getDoc(walletRef);

            let currentBalance = 0;
            if (walletSnap.exists()) {
                const walletData = walletSnap.data();
                currentBalance = walletData.balance || 0;
            }

            const creditAmount = selectedOrder.UniteeTotalPrice || selectedOrder.total || 0;
            const balanceBefore = currentBalance;
            const balanceAfter = balanceBefore + creditAmount;

            const transactionData = {
                type: 'Order Cancellation',
                amount: creditAmount,
                balanceBefore,
                balanceAfter,
                timestamp: Timestamp.now(),
                CNtype: "nonGSTcreditNote",
                orderId: selectedOrder.id,
                reason: 'Order Cancellation'
            };

            const transactionsCollection = collection(
                db, 
                'users', 
                user.uid, 
                'wallet', 
                'walletDetails', 
                'transactions'
            );
            await addDoc(transactionsCollection, transactionData);

            await updateDoc(walletRef, {
                balance: balanceAfter,
                lastUpdated: Timestamp.now()
            });

            setPaidOrders(prevOrders =>
                prevOrders.map(order =>
                    order.id === selectedOrder.id
                        ? { ...order, status: 'cancelled' }
                        : order
                )
            );

            setFilteredOrders(prevFiltered =>
                prevFiltered.map(order =>
                    order.id === selectedOrder.id
                        ? { ...order, status: 'cancelled' }
                        : order
                )
            );

            alert("Order cancelled successfully and amount credited to wallet!");
            closeModal();

        } catch (error) {
            console.error("Error cancelling order:", error);
            alert("Failed to cancel order. Please try again.");
        } finally {
            setProcessingCancel(false);
        }
    };

    const saveOrderChanges = async () => {
        if (!selectedOrder) return;

        try {
            setProcessingSave(true);

            const oldOrderRef = doc(db, 'users', user.uid, 'orders', selectedOrder.id);
            await deleteDoc(oldOrderRef);

            const ordersCollection = collection(db, 'users', user.uid, 'orders');
            const newOrderId = `edit_${selectedOrder.id}`;
            const newOrderRef = doc(ordersCollection, newOrderId);
            const newOrderData = {
                ...editedOrderData,
                id: newOrderId,
                order_id: newOrderId,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
                originalOrderId: selectedOrder.id
            };
            await setDoc(newOrderRef, newOrderData);

            try {
                const response = await axios.post('https://ufb-1.onrender.com/create-shipmozo-order', {
                    ...editedOrderData,
                    id: newOrderId,
                    order_id: newOrderId,
                    user_uid: user.uid
                });

                console.log('New Shiprocket order created:', response.data);

            } catch (apiError) {
                console.error("Error creating Shiprocket order:", apiError);
            }

            setPaidOrders(prevOrders => {
                const filtered = prevOrders.filter(order => order.id !== selectedOrder.id);
                return [{ id: newOrderId, ...newOrderData }, ...filtered];
            });

            setFilteredOrders(prevFiltered => {
                const filtered = prevFiltered.filter(order => order.id !== selectedOrder.id);
                return [{ id: newOrderId, ...newOrderData }, ...filtered];
            });

            alert("Order updated successfully!");
            closeModal();

        } catch (error) {
            console.error("Error updating order:", error);
            alert("Failed to update order. Please try again.");
        } finally {
            setProcessingSave(false);
        }
    };

// Load paid orders on component mount
    useEffect(() => {
        // Only run the initial fetch if no filters are set.
        if (!startDate && !endDate && !searchTerm) {
            setLastVisible(null);
            setHasMore(true);
            setFilteredHasMore(true);
            setAllOrdersLoaded(false);
            fetchPaidOrders();
        }
    }, [user]);
// This single useEffect handles all filtering and fetching logic
useEffect(() => {
    const hasDateFilter = startDate || endDate;
    const hasSearchTerm = searchTerm.trim() !== '';

    // This effect should not run on the initial load if paidOrders is empty,
    // as the initial fetch is handled by the first useEffect.
    // We can add a check to prevent it from overwriting the initial load.
    if (!paidOrders.length && !hasDateFilter && !hasSearchTerm) {
        return;
    }

    // Reset pagination whenever filters change
    setLastVisible(null);
    setFilteredHasMore(true);

    if (hasDateFilter) {
        // If there's a date filter, always fetch from the database.
        fetchPaidOrders(false, true);
    } else if (hasSearchTerm) {
        // If no date filter but there's a search term, run the search.
        searchOrderById(searchTerm);
    } else {
        // If no filters at all, reset the displayed orders to the master list.
        setFilteredOrders(paidOrders);
        setHasMore(true); // Restore pagination for the full list
    }
}, [startDate, endDate, searchTerm]); // <-- Dependency array is now correct

// To ensure the filtered list is in sync when the master list changes initially
// or is cleared, you can add a separate, simpler effect.
useEffect(() => {
    // If there are no active filters, the displayed list should mirror the master list.
    const noFilters = !startDate && !endDate && !searchTerm.trim();
    if (noFilters) {
        setFilteredOrders(paidOrders);
    }
}, [paidOrders]);


    if (loading && !loadingMore && !isSearching) {
        return (
            <div className="flex h-screen bg-white">
                <Sidebar />
                <div className="flex-1 flex flex-col min-w-0 overflow-hidden md:ml-64">
                    <Header />
                    <div className="flex-1 flex items-center justify-center">
                        <div className="text-lg">Loading paid orders...</div>
                    </div>
                </div>
            </div>
        );
    }

    // Determine what to show for load more button
    const shouldShowLoadMore = () => {
        const hasActiveFilters = startDate || endDate || searchTerm.trim();
        
        if (hasActiveFilters) {
            if (startDate && endDate) {
                return filteredHasMore && !loadingMore;
            } else if (searchTerm.trim()) {
                return false; // Don't show load more for search
            }
            return false;
        } else {
            return hasMore && !loadingMore;
        }
    };

    const getNoMoreOrdersMessage = () => {
        const hasActiveFilters = startDate || endDate || searchTerm.trim();
        
        if (hasActiveFilters) {
            if (startDate && endDate) {
                return "No more orders found for the selected date range.";
            } else if (searchTerm.trim()) {
                return "No more orders found for the search term.";
            }
            return "No more orders found with current filters.";
        } else {
            return "No more orders to load.";
        }
    };

    return (
        <div className="flex h-screen bg-white">
            <Sidebar />
            
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden md:ml-64">
                <Header />

                <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
                    <div className="max-w-full">
                        <div className="flex items-center justify-between mb-6">
    <h1 className="text-left text-3xl font-bold text-gray-800 order-management-title mb-2 uppercase">Paid Orders</h1>
    <span className="text-sm font-medium text-gray-500">
        {/* This line displays the count of orders currently visible */}
        Showing {filteredOrders.length} orders
    </span>
</div>
                        
                        
                        {/* Responsive Filters Container */}
                        <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
                            <div className="flex flex-col space-y-4 lg:flex-row lg:space-y-0 lg:space-x-4 lg:items-end">
                                {/* Search Bar */}
                                <div className="flex-1 lg:max-w-md">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Search Orders
                                    </label>
                                    <input 
                                        type="text" 
                                        placeholder="Search by Order ID or Store Order ID" 
                                        value={searchTerm} 
                                        onChange={handleSearchChange} 
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                
{/* Date Filters */}
<div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
    <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 mb-2">
            From Date
        </label>
        <input 
            type="date" 
            value={startDate}
            // Add 'max' to prevent selecting a date after the end date
            max={endDate || new Date().toISOString().split('T')[0]} 
            onChange={(e) => setStartDate(e.target.value)} 
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
    </div>
    
    <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 mb-2">
            To Date
        </label>
        <input 
            type="date" 
            value={endDate}
            // Add 'min' to prevent selecting a date before the start date
            min={startDate} 
            onChange={(e) => setEndDate(e.target.value)} 
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
    </div>
</div>
                            </div>
                        </div>
                        
                        {isSearching && (
                            <div className="text-center py-4">
                                <div className="inline-flex items-center space-x-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                    <span>Searching for orders...</span>
                                </div>
                            </div>
                        )}
                        
                        {filteredOrders.length > 0 ? (
                            <>
                                {/* Responsive Table Container */}
                                <div className="bg-white rounded-lg shadow overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Order ID
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Unitee Order ID
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Order Date
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        SKU
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Quantity
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Invoice
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Tracking Status
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {filteredOrders.map((order) => (
                                                    <tr key={order.id} className="hover:bg-gray-50">
                                                        <td 
                                                            className="px-4 py-4 text-sm text-blue-600 cursor-pointer hover:text-blue-800 font-medium"
                                                            onClick={() => openOrderModal(order)}
                                                        >
                                                            <div className="truncate max-w-32">
                                                                {order.Storeorder_id || 'NA'}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-4 text-sm text-gray-900">
                                                            <div className="truncate max-w-32">
                                                                {order.id || 'NA'}
                                                            </div>
                                                        </td>
                                                 <td className="px-4 py-4 text-sm text-gray-900">
    {order.updatedAt 
        ? order.updatedAt.toDate().toLocaleDateString('en-GB') 
        : 'NA'}
</td>
                                                        <td className="px-4 py-4 text-sm text-gray-900">
                                                            <div className="space-y-1">
                                                                {order.order_items?.map((item, index) => (
                                                                    <div key={index} className="truncate max-w-24 text-xs">
                                                                        {item.sku}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-4 text-sm text-gray-900">
                                                            <div className="space-y-1">
                                                                {order.order_items?.map((item, index) => (
                                                                    <div key={index} className="text-xs">
                                                                        {item.units}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-4 text-sm">
                                                            {order.invoiceUrl ? (
                                                                <button 
                                                                    onClick={() => handleDownloadInvoice(order.invoiceUrl)} 
                                                                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                                                >
                                                                    Download
                                                                </button>
                                                            ) : (
                                                                <span className="text-xs text-gray-500">No Invoice</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-4 text-sm text-blue-600">
                                                            {order.tracking_status || 'NA'}
                                                        </td>
                                                        <td className="px-4 py-4 text-sm">
                                                            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                                                                <button
                                                                    onClick={(e) => handleTrackOrder(order.id, e)}
                                                                    disabled={trackingLoading[order.id]}
                                                                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    {trackingLoading[order.id] ? (
                                                                        <>
                                                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                                                            Loading
                                                                        </>
                                                                    ) : (
                                                                        'Track Order'
                                                                    )}
                                                                </button>
                                                                
                                                                <button
                                                                    onClick={(e) => handleRaiseTicket(order, e)}
                                                                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                                                >
                                                                    Raise Ticket
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                
                                {/* Enhanced Load More Section */}
                                <div className="mt-6 flex flex-col items-center space-y-4">
                                    {shouldShowLoadMore() ? (
                                        <button 
                                            onClick={handleLoadMore}
                                            disabled={loadingMore}
                                            className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {loadingMore ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                    Loading More Orders...
                                                </>
                                            ) : (
                                                'Load More Orders'
                                            )}
                                        </button>
                                    ) : (
                                        filteredOrders.length > 0 && !shouldShowLoadMore() && (
                                            <div className="text-center text-gray-500 text-sm py-4">
                                                {getNoMoreOrdersMessage()}
                                            </div>
                                        )
                                    )}
                                    
                                    {loadingMore && (
                                        <div className="text-center text-gray-500 text-sm">
                                            <div className="inline-flex items-center space-x-2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                <span>Loading more orders...</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="bg-white rounded-lg shadow p-8 text-center">
                                <div className="text-gray-500">
                                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p className="text-lg font-medium">
                                        {isSearching ? 'Searching...' : 'No paid orders found'}
                                    </p>
                                    {(startDate || endDate || searchTerm) && !isSearching && (
                                        <p className="text-sm text-gray-400 mt-2">
                                            Try adjusting your search criteria or date range
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Ticket Modal */}
            <RaiseTicketModal
                isOpen={isTicketModalOpen}
                onClose={closeTicketModal}
                orderId={selectedOrderForTicket?.id}
                brandId={selectedOrderForTicket?.brandId || 'N/A'}
            />
            
            {/* Enhanced Order Details Modal */}
            {isModalOpen && selectedOrder && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
                        <div className="flex items-center justify-between border-b pb-4 mb-6">
                            <h2 className="text-xl font-semibold text-gray-900">Order Details</h2>
                            <button 
                                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                                onClick={closeModal}
                            >
                                ×
                            </button>
                        </div>
                        
                        <div className="max-h-96 overflow-y-auto">
                            <div className="space-y-6">
                                {/* Order IDs */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Store Order ID:</label>
                                        <input 
                                            type="text" 
                                            value={editedOrderData.Storeorder_id || ''} 
                                            disabled={modalType !== 'edit'}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Unitee Order ID:</label>
                                        <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-600">
                                            {selectedOrder.id}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Customer Information */}
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Billing Information</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name:</label>
                                            <input 
                                                type="text" 
                                                value={editedOrderData.shipping_customer_name || ''} 
                                                onChange={(e) => handleOrderDataChange('shipping_customer_name', e.target.value)}
                                                disabled={modalType !== 'edit'}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Last Name:</label>
                                            <input 
                                                type="text" 
                                                value={editedOrderData.shipping_last_name || ''} 
                                                onChange={(e) => handleOrderDataChange('shipping_last_name', e.target.value)}
                                                disabled={modalType !== 'edit'}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Email:</label>
                                            <input 
                                                type="email" 
                                                value={editedOrderData.shipping_email || ''} 
                                                onChange={(e) => handleOrderDataChange('shipping_email', e.target.value)}
                                                disabled={modalType !== 'edit'}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Phone:</label>
                                            <input 
                                                type="text" 
                                                value={editedOrderData.shipping_phone || ''} 
                                                onChange={(e) => handleOrderDataChange('shipping_phone', e.target.value)}
                                                disabled={modalType !== 'edit'}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Address Information */}
                                <div>
                                    <div className="grid grid-cols-1 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Address:</label>
                                            <input 
                                                type="text" 
                                                value={editedOrderData.shipping_address || ''} 
                                                onChange={(e) => handleOrderDataChange('shipping_address', e.target.value)}
                                                disabled={modalType !== 'edit'}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                            />
                                        </div>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">City:</label>
                                                <input 
                                                    type="text" 
                                                    value={editedOrderData.shipping_city || ''} 
                                                    onChange={(e) => handleOrderDataChange('shipping_city', e.target.value)}
                                                    disabled={modalType !== 'edit'}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                                />
                                            </div>
                                            
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">State:</label>
                                                <input 
                                                    type="text" 
                                                    value={editedOrderData.shipping_state || ''} 
                                                    onChange={(e) => handleOrderDataChange('shipping_state', e.target.value)}
                                                    disabled={modalType !== 'edit'}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                                />
                                            </div>
                                            
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Pincode:</label>
                                                <input 
                                                    type="text" 
                                                    value={editedOrderData.shipping_pincode || ''} 
                                                    onChange={(e) => handleOrderDataChange('shipping_pincode', e.target.value)}
                                                    disabled={modalType !== 'edit'}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                                />
                                            </div>
                                            
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Country:</label>
                                                <input 
                                                    type="text" 
                                                    value={editedOrderData.shipping_country || ''} 
                                                    onChange={(e) => handleOrderDataChange('shipping_country', e.target.value)}
                                                    disabled={modalType !== 'edit'}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Payment Information */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method:</label>
                                        <input 
                                            type="text" 
                                            value={editedOrderData.payment_method || ''} 
                                            disabled
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                                        />
                                    </div>
                                    
                                    {editedOrderData.payment_method === 'COD' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">COD Amount to Collect:</label>
                                            <input
                                                type="text"
                                                value={editedOrderData.total || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                This will be the final amount to collect from the customer
                                            </p>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Order Items */}
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Order Items</h3>
                                    <div className="space-y-6">
                                        {editedOrderData.order_items && editedOrderData.order_items.map((item, index) => {
                                            const skuParts = item.sku ? item.sku.split('-') : [];
                                            const baseSku = skuParts.slice(0, 2).join('-');
                                            const currentSize = skuParts[2] || '';
                                            const extraParts = skuParts.slice(3);
                                            const availableSizes = ['XS','S', 'M', 'L','XL','XXL'];

                                            const handleSizeChange = (newSize) => {
                                                const newSku = [baseSku, newSize, ...extraParts].join('-');
                                                handleLineItemChange(index, 'sku', newSku);

                                                 const currentName = item.name || '';
    const sizePattern = /\s*-\s*(XS|S|M|L|XL|XXL)$/i;
    
    if (sizePattern.test(currentName)) {
        // Replace existing size with new size
        const newName = currentName.replace(sizePattern, ` - ${newSize}`);
        handleLineItemChange(index, 'name', newName);
    }
                                            };
                                            
                                            return (
                                                <div key={index} className="border rounded-lg p-4 bg-gray-50">
                                                    {/* Mockup Images */}
                                                    {(item.design?.frontImage || item.design?.backImage) && (
                                                        <div className="flex space-x-4 mb-4">
                                                            {item.design?.frontImage && (
                                                                <div className="text-center">
                                                                    <label className="block text-xs text-gray-600 mb-1">Front:</label>
                                                                    <img 
                                                                        src={item.design.frontImage} 
                                                                        alt="Front mockup" 
                                                                        className="w-16 h-16 object-cover border border-gray-300 rounded"
                                                                    />
                                                                </div>
                                                            )}
                                                            {item.design?.backImage && (
                                                                <div className="text-center">
                                                                    <label className="block text-xs text-gray-600 mb-1">Back:</label>
                                                                    <img 
                                                                        src={item.design.backImage} 
                                                                        alt="Back mockup" 
                                                                        className="w-16 h-16 object-cover border border-gray-300 rounded"
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    
                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">SKU:</label>
                                                            <input 
                                                                type="text" 
                                                                value={item.sku || ''} 
                                                                disabled
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                                                            />
                                                        </div>
                                                        
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">Units:</label>
                                                            <input 
                                                                type="number" 
                                                                value={item.units || ''} 
                                                                disabled={modalType !== 'edit'}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                                            />
                                                        </div>
                                                        
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">Name:</label>
                                                            <input 
                                                                type="text" 
                                                                value={item.name || ''} 
                                                                disabled
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                                                            />
                                                        </div>
                                                        
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">Price:</label>
                                                            <input 
                                                                type="text" 
                                                                value={item.selling_price || ''} 
                                                                disabled
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                                                            />
                                                        </div>
                                                        
                                                        {modalType === 'edit' && (
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">Size:</label>
                                                                <select
                                                                    value={currentSize}
                                                                    onChange={(e) => handleSizeChange(e.target.value)}
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                >
                                                                    {availableSizes.map(size => (
                                                                        <option key={size} value={size}>{size}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Modal Footer */}
                        <div className="border-t pt-4 mt-6">
                            {modalType === 'view' && (
                                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                                    <button 
                                        onClick={() => setModalType('cancel')}
                                        className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                    >
                                        Cancel Order
                                    </button>
                                    <button 
                                        onClick={() => setModalType('edit')}
                                        className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        Edit Order
                                    </button>
                                </div>
                            )}
                            
                            {modalType === 'cancel' && (
                                <div>
                                    <p className="text-red-600 mb-4 text-sm">
                                        Are you sure you want to cancel this order? The amount will be credited to your wallet.
                                    </p>
                                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                                        <button 
                                            onClick={handleCancelOrder}
                                            disabled={processingCancel}
                                            className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {processingCancel ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                    Cancelling...
                                                </>
                                            ) : (
                                                'Confirm Cancel'
                                            )}
                                        </button>
                                        <button 
                                            onClick={() => setModalType('view')}
                                            className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        >
                                            Back
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            {modalType === 'edit' && (
                                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                                    <button 
                                        onClick={saveOrderChanges}
                                        disabled={processingSave}
                                        className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {processingSave ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                Saving...
                                            </>
                                        ) : (
                                            'Save Changes'
                                        )}
                                    </button>
                                    <button 
                                        onClick={() => {
                                            setModalType('view');
                                            setEditedOrderData({ ...selectedOrder });
                                        }}
                                        className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        Cancel Edit
                                    </button>
                                </div>
                            )}
                            
                            {modalType === 'view_only' && (
                                <p className="text-yellow-600 font-medium text-sm">
                                    This order has been shipped and cannot be cancelled or edited.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaidOrdersPage;